// vue
import { ref, computed, watch, ComputedRef, onMounted, onUnmounted } from 'vue';

// types
import { Props, Context } from '../../../types';

// chart interface
import type { ChartPropertiesOverrides } from 'charting_library';

// store
import { useGl } from '@store/ts/gl';
import { useRefs } from '@store/ts/refs';
import { IExchange } from '@/types/exchanges';

export function useComputed(props: Props) {
    // store
    const gl = useGl();
    const refs = useRefs();

    const theme = computed(() => gl.darkTheme ? 'dark' : 'light');

    const exchangeCodeName: ComputedRef<string> = computed(() => {
        const item = refs.exchanges.find(el => el.id == props.exchange);
        if (item) {
            return item?.code_name;
        }

        return 'binance';
    });

    const exchangeFullInfo: ComputedRef<IExchange> = computed(() => {
        return refs.exchanges.find(el => el.id == props.exchange) ?? {} as IExchange;
    });
    
    const hardHideChart: ComputedRef<boolean> = computed(() => !exchangeFullInfo.value.ws_kline && !exchangeFullInfo.value.kline_remote);

    const customThemes = {
        light: {
            'color1':['#eef2ff','#dce6ff','#cbd9ff','#b9ccff','#a8bfff','#97b3ff','#85a6ff','#7499ff','#628cff','#5180ff','#2e66ff','#2a5eea','#2655d5','#234dbf','#1f44aa','#1b3c95','#173380','#132b6a','#0f2255','#0c1a40'],
            'color2':['#f4f4f5','#e9e9eb','#dddee1','#d2d3d7','#c7c8cd','#bcbdc3','#b0b2b8','#a5a7ae','#9a9ca4','#8f919a','#787b86','#6e717b','#646770','#5a5c65','#505259','#46484e','#3c3e43','#202020','#28292d','#1e1f22'],
            'color3':['#feeef0','#B90814FF','#fccdd1','#fbbcc1','#faabb2','#f99ba2','#f78a93','#f67983','#f56874','#f45864','#f23645','#de323f','#ca2d3a','#b62934','#a1242e','#8d2028','#791b23','#65171d','#511217','#3d0e11'],
            'color4':['#eaf7f5','#d6eeea','#c1e6e0','#adddd5','#98d5cb','#84ccc0','#6fc4b6','#5abbab','#46b3a1','#31aa96','#089981','#078c76','#07806c','#067361','#056656','#05594b','#044d41','#034036','#03332b','#022620'],
            'color5':['#fff6ea','#ffeed5','#ffe5bf','#ffddaa','#ffd495','#ffcc80','#ffc36a','#ffba55','#ffb240','#ffa92b','#ff9800','#ea8b00','#d57f00','#bf7200','#aa6500','#955900','#804c00','#6a3f00','#553300','#402600'],
            'color6':['#f7edf8','#efdbf2','#e6c9eb','#deb7e5','#d6a5de','#ce93d8','#c581d1','#bd6fca','#b55dc4','#ad4bbd','#9c27b0','#8f24a1','#822193','#751d84','#681a75','#5b1767','#4e1458','#411049','#340d3b','#270a2c'],
            'color7':['#ffffff','#ffffff','#ffffff','#ffffff','#ffffff','#ffffff','#ffffff','#ffffff','#ffffff','#ffffff','#ffffff','#eaeaea','#d5d5d5','#bfbfbf','#aaaaaa','#959595','#808080','#6a6a6a','#555555','#404040'],
            'white':'#ffffff',
            'black':'#000000',
        },
        dark: {
            'color1':['#eef2ff','#dce6ff','#cbd9ff','#b9ccff','#a8bfff','#97b3ff','#85a6ff','#7499ff','#628cff','#5180ff','#2e66ff','#2a5eea','#2655d5','#234dbf','#1f44aa','#1b3c95','#173380','#132b6a','#0f2255','#0c1a40'],
            'color2':['#f4f4f5','#e9e9eb','#dddee1','#d2d3d7','#c7c8cd','#bcbdc3','#b0b2b8','#a5a7ae','#9a9ca4','#8f919a','#787b86','#6e717b','#646770','#5a5c65','#505259','#46484e','#3c3e43','#202020','#28292d','#1e1f22'],
            'color3':['#feeef0','#fddee0','#fccdd1','#fbbcc1','#faabb2','#f99ba2','#f78a93','#f67983','#f56874','#f45864','#f23645','#de323f','#ca2d3a','#b62934','#a1242e','#8d2028','#791b23','#65171d','#511217','#3d0e11'],
            'color4':['#eaf7f5','#d6eeea','#c1e6e0','#adddd5','#98d5cb','#84ccc0','#6fc4b6','#5abbab','#46b3a1','#31aa96','#089981','#078c76','#07806c','#067361','#056656','#05594b','#044d41','#034036','#03332b','#022620'],
            'color5':['#fff6ea','#ffeed5','#ffe5bf','#ffddaa','#ffd495','#ffcc80','#ffc36a','#ffba55','#ffb240','#ffa92b','#ff9800','#ea8b00','#d57f00','#bf7200','#aa6500','#955900','#804c00','#6a3f00','#553300','#402600'],
            'color6':['#f7edf8','#efdbf2','#e6c9eb','#deb7e5','#d6a5de','#ce93d8','#c581d1','#bd6fca','#b55dc4','#ad4bbd','#9c27b0','#8f24a1','#822193','#751d84','#681a75','#5b1767','#4e1458','#411049','#340d3b','#270a2c'],
            'color7':['#ececec','#dadada','#c7c7c7','#b5b5b5','#a2a2a2','#909090','#7d7d7d','#6a6a6a','#585858','#454545','#202020','#1d1d1d','#1b1b1b','#181818','#151515','#131313','#101010','#0d0d0d','#0b0b0b','#080808'],
            'white':'#ffffff',
            'black':'#000000',
        },
    };

    // Общие настройки
    const commonOverrides = {
        // Свечи
        'mainSeriesProperties.candleStyle.upColor': '#059669', // Бычьи свечи (зеленые)
        'mainSeriesProperties.candleStyle.downColor': '#EF4444', // Медвежьи свечи (красные)
        'mainSeriesProperties.candleStyle.borderUpColor': '#059669', // Границы бычьих свечей
        'mainSeriesProperties.candleStyle.borderDownColor': '#EF4444', // Границы медвежьих свечей
        'mainSeriesProperties.candleStyle.wickUpColor': '#059669', // Тени бычьих свечей
        'mainSeriesProperties.candleStyle.wickDownColor': '#EF4444', // Тени медвежьих свечей

        // Линии индикаторов
        'mainSeriesProperties.haStyle.upColor': '#059669',
        'mainSeriesProperties.haStyle.downColor': '#EF4444',
        'mainSeriesProperties.haStyle.borderUpColor': '#059669',
        'mainSeriesProperties.haStyle.borderDownColor': '#EF4444',
        'mainSeriesProperties.haStyle.wickUpColor': '#059669',
        'mainSeriesProperties.haStyle.wickDownColor': '#EF4444',

        // Линии индикаторов (SMA, EMA)
        'mainSeriesProperties.lineStyle.color': '#2962FF',

        // Выделение последней цены
        'scalesProperties.showSeriesLastValue': true,
        'scalesProperties.lastValueVisible': true,

        // Линия цены
        'mainSeriesProperties.priceLineColor': '#2E2E2E',
        'mainSeriesProperties.priceLineWidth': 1,

        // Показывать аргументы индикаторов
        'paneProperties.legendProperties.showStudyArguments': true,
        // Показывать названия индикаторов
        'paneProperties.legendProperties.showStudyTitles': true,
        // Показывать значения индикаторов
        'paneProperties.legendProperties.showStudyValues': true,
        // Показывать название серии
        'paneProperties.legendProperties.showSeriesTitle': true,
        // Показывать OHLC
        'paneProperties.legendProperties.showSeriesOHLC': true,

        // Курсор
        'paneProperties.crossHairProperties.style': 2,
    };

    // Настройки для светлой темы
    const lightOverrides: Partial<ChartPropertiesOverrides> = {
        ...commonOverrides,
        // Фон графика
        'paneProperties.background': '#FFFFFF',
        'paneProperties.backgroundType': 'solid',

        // Цвет текста шкал (оси X и Y)
        'scalesProperties.textColor': '#2E2E2E',

        // Линии сетки (сделал серыми, чтобы не раздражали глаза)
        'paneProperties.vertGridProperties.color': '#E0E0E0',
        'paneProperties.horzGridProperties.color': '#E0E0E0',

        // Линия перекрестия
        'paneProperties.crossHairProperties.color': '#A0A0A0',

        // Цвет оси времени и цены
        'scalesProperties.lineColor': '#D4D4D4',
    };

    // Настройки для темной темы
    const darkOverrides: Partial<ChartPropertiesOverrides> = {
        ...commonOverrides,
        // Фон графика
        'paneProperties.background': '#202020',
        'paneProperties.backgroundType': 'solid',

        // Цвет фона левой панели и хедера
        'header.backgroundColor': '#202020',  // Фон хедера
        'leftPanel.backgroundColor': '#202020',  // Фон левой панели

        // Цвет текста шкал (оси X и Y)
        'scalesProperties.textColor': '#E0E0E0',

        // Линии сетки (приглушённые, чтобы не резали глаза)
        'paneProperties.vertGridProperties.color': '#2E2E2E',
        'paneProperties.horzGridProperties.color': '#2E2E2E',

        // Линия перекрестия
        'paneProperties.crossHairProperties.color': '#A0A0A0',

        // Цвет оси времени и цены
        'scalesProperties.lineColor': '#3A3A3A',

        // Линии индикаторов (SMA, EMA)
        'mainSeriesProperties.lineStyle.color': '#1A6DE0',

        // Линия цены
        'mainSeriesProperties.priceLineColor': '#E0E0E0',
        'mainSeriesProperties.priceLineWidth': 1,

        // Цвета кнопок и активных элементов
        'scalesProperties.iconColor': '#1A6DE0',
        'mainSeriesProperties.titleColor': '#1A6DE0',
        'paneProperties.legendProperties.showLegend': true,
    };

    const overrides = computed(() => gl.darkTheme ? darkOverrides : lightOverrides);
    // пара
    const pair: ComputedRef<string> = computed(() => {
        const hasSlash = props.data.pair.split('/');
        return hasSlash.length > 1
            ? `${hasSlash[0].toUpperCase()}${hasSlash[1].toUpperCase()}`
            : hasSlash[0].toUpperCase();
    });

    return {
        pair,
        theme,
        overrides,
        customThemes,
        exchangeCodeName,
        exchangeFullInfo,
        hardHideChart,
    };
}